// Auswahl einer Kennzahl (R-06): Suchfeld, Gruppen (Leads, Anrufe, E-Mail und SMS …), eine Zeile je Kennzahl mit Untertitel.
import { useEffect, useMemo, useRef, useState } from "react";
import type { Kennzahl } from "../api/berichte";
import { Ausklapp } from "../components/Ausklapp";
import { Icon } from "../components/Icon";

const norm = (s: string) => s.toLocaleLowerCase("de").normalize("NFD").replace(/[̀-ͯ]/g, "");

interface Props {
  katalog: Kennzahl[];
  anker: HTMLElement | null;
  offen: boolean;
  onSchliessen: () => void;
  onWahl: (id: string) => void;
  /** schon gezeigte Kennzahlen (ausgegraut, nicht wählbar) */
  ausgeschlossen?: Set<string>;
  /** gerade gewählte (Haken) */
  aktuell?: string | null;
}

export function KennzahlWahl({ katalog, anker, offen, onSchliessen, onWahl, ausgeschlossen, aktuell }: Props) {
  const [suche, setSuche] = useState("");
  const feld = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (offen) {
      setSuche("");
      window.setTimeout(() => feld.current?.focus(), 0);
    }
  }, [offen]);
  const gruppen = useMemo(() => {
    const n = norm(suche.trim());
    const treffer = n ? katalog.filter((k) => norm(`${k.titel} ${k.untertitel ?? ""} ${k.gruppe}`).includes(n)) : katalog;
    const nach = new Map<string, Kennzahl[]>();
    for (const k of treffer) nach.set(k.gruppe, [...(nach.get(k.gruppe) ?? []), k]);
    return [...nach.entries()];
  }, [katalog, suche]);
  return (
    <Ausklapp anker={anker} offen={offen} onSchliessen={onSchliessen} breite={320} rolle="dialog" maxHoehe={440}>
      <div className="kennzahlwahl">
        <div className="kennzahlwahl__suche">
          <Icon name="suche" groesse={14} />
          <input ref={feld} className="kennzahlwahl__eingabe" placeholder="Kennzahl suchen …" value={suche} onChange={(e) => setSuche(e.target.value)} aria-label="Kennzahl suchen" />
        </div>
        <div className="kennzahlwahl__liste" role="listbox" aria-label="Kennzahlen">
          {gruppen.length === 0 ? <p className="leise kennzahlwahl__leer">Keine Kennzahl gefunden.</p> : null}
          {gruppen.map(([gruppe, liste]) => (
            <div key={gruppe}>
              <div className="kennzahlwahl__gruppe">{gruppe}</div>
              {liste.map((k) => {
                const gesperrt = !!ausgeschlossen?.has(k.id) && k.id !== aktuell;
                return (
                  <button
                    key={k.id}
                    type="button"
                    role="option"
                    aria-selected={k.id === aktuell}
                    disabled={gesperrt}
                    title={k.beschreibung || undefined}
                    className={`kennzahlwahl__zeile${k.id === aktuell ? " kennzahlwahl__zeile--an" : ""}`}
                    onClick={() => {
                      onWahl(k.id);
                      onSchliessen();
                    }}
                  >
                    <span className="kennzahlwahl__titel">{k.titel}</span>
                    <span className="kennzahlwahl__unter">{k.untertitel?.toLocaleLowerCase("de")}</span>
                    {k.id === aktuell ? <Icon name="haken" groesse={13} /> : gesperrt ? <span className="leise">schon da</span> : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </Ausklapp>
  );
}
